import { Box, Stack, Theme } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import Loader from '@/components/loader';
import { createUseStyles } from 'react-jss';
import Text from '@/components/text';
import useUserSettings from '@/hooks/use-user-settings.hook';
import ScrollBox from '@/components/scroll-box';
import SearchInput from '@/components/search-input';
import useThread from '@/hooks/use-thread.hook';
import FlatButton from '@/components/flat-button';
import FloatLabelCard from '@/components/float-label-card';
import classNames from 'classnames';
import { getBreakpoint } from '@/theme';
import BaseSwitch from '@/components/form/base-switch';

interface Props {
  className?: string;
}

const useStyles = createUseStyles<string, { hasScrollbars: boolean }>((theme: Theme) => ({
  floatCard: {
    paddingRight: ({ hasScrollbars }) => (hasScrollbars ? 4 : 8),
    width: 300,
  },
  toolbar: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: '0px 2px',
    marginBottom: 4,
    width: '100%',
    boxSizing: 'border-box',
  },
  item: {
    width: '100%',
    padding: '4px 0px',
    '&.last': {
      paddingBottom: 0,
    },
  },
  [getBreakpoint('md')]: {
    floatCard: {
      width: '100%',
    },
  },
}));

const DATASOURCE_LIST_MAX = 4;

const DatasourceList: FC<Props> = ({ className }) => {
  const [selectAll, setSelectAll] = useState(false);
  const [keyword, setKeyword] = useState('');

  const { datasources, isLoading } = useUserSettings();
  const { toggleDatasource, settings: threadSettings } = useThread();

  // the thread-level setting for public internet search is a button that lives outside this tray, so the in-tray list of datasources should just handle non-public datasources
  const nonPublicDatasources = useMemo(
    () => datasources.filter(({ isPublicInternet }) => !isPublicInternet),
    [datasources]
  );

  const filteredDatasources = useMemo(() => {
    const safeKeyword = String(keyword).trim().toLowerCase();

    return nonPublicDatasources.filter(({ name, description }) => {
      const keywordMatch =
        String(name).toLowerCase().includes(safeKeyword) ||
        String(description).toLowerCase().includes(safeKeyword);

      return keywordMatch;
    });
  }, [nonPublicDatasources, keyword]);

  const hasScrollbars = filteredDatasources.length > DATASOURCE_LIST_MAX;
  const styles = useStyles({ hasScrollbars });

  const handleToggleDatasource = (id: string, enabled: boolean) => {
    toggleDatasource(id, enabled);
    const allSelected = threadSettings.datasources.every(
      ({ id: filteredId, enabled: filterEnabled, isPublicInternet }) =>
        (enabled && filteredId === id) || filterEnabled || isPublicInternet
    );
    setSelectAll(allSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    const datasourceIds = filteredDatasources.map(({ id }) => id);
    toggleDatasource(datasourceIds, checked);
    setSelectAll(checked);
  };

  const isEnabled = useCallback(
    (id: string) => {
      return threadSettings.datasources.some(({ id: dsId, enabled }) => dsId === id && enabled);
    },
    [threadSettings.datasources]
  );

  useEffect(() => {
    setSelectAll(filteredDatasources.every(({ id }) => isEnabled(id)));
  }, [filteredDatasources, isEnabled]);

  const hasNonPublicDatasources = !!nonPublicDatasources.length;
  const hasDatasources = !!filteredDatasources.length;
  const noResults = !isLoading && !!keyword.length && !hasDatasources;
  const canSelectAll = hasDatasources;
  const compClass = classNames(styles.floatCard, className);

  return (
    <FloatLabelCard label="Enterprise Data Sources" className={compClass}>
      <Box mt={0.5} width="100%">
        {isLoading && <Loader />}

        {!isLoading && (
          <>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              className={styles.toolbar}
            >
              <Box>
                <SearchInput
                  value={keyword}
                  onChange={setKeyword}
                  disabled={!hasNonPublicDatasources}
                />
              </Box>
              <Box>
                <FlatButton
                  label={selectAll ? 'Disable All' : 'Enable All'}
                  onClick={() => handleSelectAll(!selectAll)}
                  size="x-small"
                  disabled={!canSelectAll}
                />
              </Box>
            </Stack>

            {!hasNonPublicDatasources && (
              <Box mt={2}>
                <Text italic size="small">
                  No data sources available
                </Text>
              </Box>
            )}

            <ScrollBox
              max={DATASOURCE_LIST_MAX}
              emptyMessage={noResults ? 'No matching data sources found' : ''}
            >
              {filteredDatasources.map(({ id, name, description }, index) => {
                const enabled = isEnabled(id);
                const hasDescription = !!description;
                const compClass = classNames(styles.item, {
                  last: index === filteredDatasources.length - 1,
                });

                return (
                  <Stack key={id} className={compClass}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box maxWidth="85%">
                        <Text size="small" bold dotdot>
                          {name}
                        </Text>
                      </Box>
                      <Stack justifyContent="center">
                        <BaseSwitch
                          checked={enabled}
                          onChange={(_, checked) => handleToggleDatasource(id, checked)}
                          size="small"
                        />
                      </Stack>
                    </Stack>

                    {hasDescription && <Text size="small">{description}</Text>}
                  </Stack>
                );
              })}
            </ScrollBox>
          </>
        )}
      </Box>
    </FloatLabelCard>
  );
};

export default DatasourceList;
